/**
 * Which invoice run bills an inventory check, and when that run generates the
 * invoice.
 *
 * Mirrors two colugo rules that decide the billing month of an inventory check:
 *
 *  - the billing period of a country —
 *    `services/InvoiceGenerator/billingPeriods.ts`. A default period is the
 *    calendar month; a Spanish period runs from the 25th of the previous month
 *    to the 24th of the billing month.
 *  - the 7 day shift `getInventoryCheckBillingDataForInvoiceMonth`
 *    (`services/InvoiceGenerator/billomat.ts`) applies to that period before it
 *    selects inventory checks: a check belongs to billing month M when its
 *    expiration date falls in
 *    [periodStart(M) - 7 days, periodEndExclusive(M) - 7 days).
 *
 * Kept in sync with colugo deliberately: colugo decides what is billed, this
 * module only names the result on the inventory check page. A change to either
 * backend rule makes the page name the wrong run.
 *
 * Not covered: `countryExcludedFromEndOfMonthInvoicing` (billingPeriods.ts)
 * also keeps US stores out of the default run, and no US run exists, so for a
 * US store this names an end-of-month date that no automatic run uses.
 */

const SPANISH_INVOICE_RUN_COUNTRY = 'ES';
const SPANISH_PERIOD_FIRST_DAY = 25;
const SPANISH_GENERATION_DAY = 25;
const INVENTORY_CHECK_WINDOW_SHIFT_DAYS = 7;

/**
 * The timezone the period boundaries are midnight in. `getBillingPeriodForCountry`
 * builds them with `moment().startOf('month')`, which is midnight in the running
 * process's timezone, and the invoice run worker spawns the generator with
 * TZ=Europe/Berlin (`scripts/InvoiceGeneration/invoiceRunWorker.ts`). Reading
 * the expiration date in the browser's timezone instead would move a check
 * across the window edge for anyone outside central Europe.
 */
const INVOICE_RUN_TIME_ZONE = 'Europe/Berlin';

export type InventoryCheckBilling = {
  billingMonthStart: Date;
  generationDate: Date;
};

type CalendarDate = { year: number; month: number; day: number };

function invoiceRunCalendarDate(instant: Date): CalendarDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: INVOICE_RUN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return {
    year: part('year'),
    month: part('month') - 1,
    day: part('day'),
  };
}

/**
 * Day of month on which the inventory check window of the expiration month
 * ends. An expiration date on or after it belongs to the following billing
 * month.
 *
 * The day always lands in the expiration month, because both periods end at
 * least 7 days after that month starts.
 */
function inventoryCheckWindowEndDay(
  { year, month }: CalendarDate,
  isSpanish: boolean,
): number {
  const windowEnd = isSpanish
    ? new Date(Date.UTC(year, month, SPANISH_PERIOD_FIRST_DAY))
    : new Date(Date.UTC(year, month + 1, 1));
  windowEnd.setUTCDate(
    windowEnd.getUTCDate() - INVENTORY_CHECK_WINDOW_SHIFT_DAYS,
  );
  return windowEnd.getUTCDate();
}

/**
 * The billing month and the invoice generation date of an inventory check.
 *
 * `invoiceRunCountry` is the store's `invoice_run_country` — colugo's own
 * `Store.getTemplateCountry`, which is what a run selects its stores by. It is
 * already normalized there (an unknown country resolves to DE), so this
 * compares it exactly: treating "es" as Spain here would name a run that will
 * not bill the check.
 */
export function getInventoryCheckBilling(
  expirationDate: Date,
  invoiceRunCountry: string,
): InventoryCheckBilling {
  const isSpanish = invoiceRunCountry === SPANISH_INVOICE_RUN_COUNTRY;
  const expiration = invoiceRunCalendarDate(expirationDate);
  const windowEndDay = inventoryCheckWindowEndDay(expiration, isSpanish);

  const billingMonth =
    expiration.month + (expiration.day >= windowEndDay ? 1 : 0);

  return {
    billingMonthStart: new Date(expiration.year, billingMonth, 1),
    generationDate: isSpanish
      ? new Date(expiration.year, billingMonth, SPANISH_GENERATION_DAY)
      : new Date(expiration.year, billingMonth + 1, 0),
  };
}
